import {
  BuyerLegalType,
  FoaPart,
  Regulation,
  type RejectionGroundDto,
  type SourcingEventDto,
} from "../../autogen/bff-api";

export interface RejectionRegime {
  regulation?: Regulation | null;
  buyerLegalType?: BuyerLegalType | null;
}

export const rejectionRegimeOf = (event?: SourcingEventDto | null): RejectionRegime => {
  const context = event?.eventContext;
  return context?.type === "Public" ? { regulation: context.regulation, buyerLegalType: context.buyerLegalType } : {};
};

export type RejectionGroundSubject = "supplier" | "tender";

interface FoaRejectionGroundGroup {
  subject: RejectionGroundSubject;
  keyPrefix: string;
  mandatoryDescription: string;
  optionalDescription: string;
  grounds: RejectionGroundDto[];
}

export type RejectionGroundGroupKind = "mandatory" | "optional" | "reasons";

export interface RejectionGroundGroup {
  kind: RejectionGroundGroupKind;
  subject: RejectionGroundSubject;
  description: string;
  grounds: RejectionGroundDto[];
}

const SUPPLIER_REJECTION_GROUND_GROUPS: FoaRejectionGroundGroup[] = [
  {
    subject: "supplier",
    keyPrefix: "foa-24-2-",
    mandatoryDescription: "FOA § 24-2 første og annet ledd",
    optionalDescription: "FOA § 24-2 tredje ledd",
    grounds: [
      {
        key: "foa-24-2-1-a",
        label: "Oppfyller ikke kvalifikasjonskravene",
        foaReference: "FOA § 24-2 første ledd bokstav a",
        mandatory: true,
      },
      {
        key: "foa-24-2-1-b",
        label:
          "Ikke oppfylt forpliktelser til å betale skatter, avgifter og trygdeavgifter",
        foaReference: "FOA § 24-2 første ledd bokstav b",
        mandatory: true,
      },
      {
        key: "foa-24-2-1-c",
        label:
          "Inhabilitet som ikke kan avhjelpes med mindre inngripende tiltak",
        foaReference: "FOA § 24-2 første ledd bokstav c",
        mandatory: true,
      },
      {
        key: "foa-24-2-1-d",
        label: "Deltatt i forberedelsen og oppnådd urimelig konkurransefordel",
        foaReference: "FOA § 24-2 første ledd bokstav d",
        mandatory: true,
      },
      {
        key: "foa-24-2-2-a",
        label: "Deltakelse i en kriminell organisasjon",
        foaReference: "FOA § 24-2 annet ledd bokstav a",
        mandatory: true,
      },
      {
        key: "foa-24-2-2-b",
        label: "Korrupsjon",
        foaReference: "FOA § 24-2 annet ledd bokstav b",
        mandatory: true,
      },
      {
        key: "foa-24-2-2-c",
        label: "Bedrageri",
        foaReference: "FOA § 24-2 annet ledd bokstav c",
        mandatory: true,
      },
      {
        key: "foa-24-2-2-d",
        label:
          "Terrorhandlinger eller straffbare handlinger med forbindelse til terroraktivitet",
        foaReference: "FOA § 24-2 annet ledd bokstav d",
        mandatory: true,
      },
      {
        key: "foa-24-2-2-e",
        label: "Hvitvasking av penger eller finansiering av terrorisme",
        foaReference: "FOA § 24-2 annet ledd bokstav e",
        mandatory: true,
      },
      {
        key: "foa-24-2-2-f",
        label: "Barnearbeid og andre former for menneskehandel",
        foaReference: "FOA § 24-2 annet ledd bokstav f",
        mandatory: true,
      },
      {
        key: "foa-24-2-3-a",
        label: "Skatter, avgifter og trygdeavgifter i andre tilfeller",
        foaReference: "FOA § 24-2 tredje ledd bokstav a",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-b",
        label:
          "Konkurs, insolvensbehandling, avvikling eller tilsvarende prosess",
        foaReference: "FOA § 24-2 tredje ledd bokstav b",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-c",
        label:
          "Alvorlige eller gjentatte brudd på miljø, arbeidsforhold eller sosiale forhold",
        foaReference: "FOA § 24-2 tredje ledd bokstav c",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-d",
        label: "Interessekonflikt som kan påvirke konkurransen",
        foaReference: "FOA § 24-2 tredje ledd bokstav d",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-e",
        label: "Avtale med hensikt å vri konkurransen",
        foaReference: "FOA § 24-2 tredje ledd bokstav e",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-f",
        label: "Vesentlig mislighold av tidligere offentlig kontrakt",
        foaReference: "FOA § 24-2 tredje ledd bokstav f",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-g",
        label: "Grovt uriktige eller misvisende opplysninger",
        foaReference: "FOA § 24-2 tredje ledd bokstav g",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-h",
        label:
          "Utilbørlig påvirkning eller tilgang til fortrolige opplysninger",
        foaReference: "FOA § 24-2 tredje ledd bokstav h",
        mandatory: false,
      },
      {
        key: "foa-24-2-3-i",
        label: "Alvorlige feil som medfører tvil om yrkesmessig integritet",
        foaReference: "FOA § 24-2 tredje ledd bokstav i",
        mandatory: false,
      },
    ],
  },
  {
    subject: "supplier",
    keyPrefix: "foa-9-5-",
    mandatoryDescription: "FOA § 9-5 første og annet ledd",
    optionalDescription: "FOA § 9-5 tredje ledd",
    grounds: [
      {
        key: "foa-9-5-1-a",
        label: "Oppfyller ikke kvalifikasjonskravene",
        foaReference: "FOA § 9-5 første ledd bokstav a",
        mandatory: true,
      },
      {
        key: "foa-9-5-1-b",
        label:
          "Inhabilitet som ikke kan avhjelpes med mindre inngripende tiltak",
        foaReference: "FOA § 9-5 første ledd bokstav b",
        mandatory: true,
      },
      {
        key: "foa-9-5-1-c",
        label: "Deltatt i forberedelsen og oppnådd urimelig konkurransefordel",
        foaReference: "FOA § 9-5 første ledd bokstav c",
        mandatory: true,
      },
      {
        key: "foa-9-5-2-a",
        label: "Deltakelse i en kriminell organisasjon",
        foaReference: "FOA § 9-5 annet ledd bokstav a",
        mandatory: true,
      },
      {
        key: "foa-9-5-2-b",
        label: "Korrupsjon",
        foaReference: "FOA § 9-5 annet ledd bokstav b",
        mandatory: true,
      },
      {
        key: "foa-9-5-2-c",
        label: "Bedrageri",
        foaReference: "FOA § 9-5 annet ledd bokstav c",
        mandatory: true,
      },
      {
        key: "foa-9-5-2-d",
        label:
          "Terrorhandlinger eller straffbare handlinger med forbindelse til terroraktivitet",
        foaReference: "FOA § 9-5 annet ledd bokstav d",
        mandatory: true,
      },
      {
        key: "foa-9-5-2-e",
        label: "Hvitvasking av penger eller finansiering av terrorisme",
        foaReference: "FOA § 9-5 annet ledd bokstav e",
        mandatory: true,
      },
      {
        key: "foa-9-5-2-f",
        label: "Barnearbeid og andre former for menneskehandel",
        foaReference: "FOA § 9-5 annet ledd bokstav f",
        mandatory: true,
      },
      {
        key: "foa-9-5-3-a",
        label: "Skatter, avgifter og trygdeavgifter",
        foaReference: "FOA § 9-5 tredje ledd bokstav a",
        mandatory: false,
      },
      {
        key: "foa-9-5-3-b",
        label:
          "Mangler nødvendig integritet eller kan ikke forventes å gjennomføre kontrakten tilfredsstillende",
        foaReference: "FOA § 9-5 tredje ledd bokstav b",
        mandatory: false,
      },
    ],
  },
];

const UTILITIES_SUPPLIER_GROUNDS: RejectionGroundDto[] = [
  {
    key: "ff-20-2-1-a",
    label: "Oppfyller ikke kvalifikasjonskravene",
    foaReference: "forsyningsforskriften § 20-2 første ledd bokstav a",
    mandatory: true,
  },
  {
    key: "ff-20-2-1-b",
    label: "Inhabilitet som ikke kan avhjelpes med mindre inngripende tiltak",
    foaReference: "forsyningsforskriften § 20-2 første ledd bokstav b",
    mandatory: true,
  },
  {
    key: "ff-20-2-1-c",
    label: "Deltatt i forberedelsen og oppnådd urimelig konkurransefordel",
    foaReference: "forsyningsforskriften § 20-2 første ledd bokstav c",
    mandatory: true,
  },
  {
    key: "ff-20-2-2-a",
    label: "Deltakelse i en kriminell organisasjon",
    foaReference: "forsyningsforskriften § 20-2 annet ledd bokstav a",
    mandatory: false,
  },
  {
    key: "ff-20-2-2-b",
    label: "Korrupsjon",
    foaReference: "forsyningsforskriften § 20-2 annet ledd bokstav b",
    mandatory: false,
  },
  {
    key: "ff-20-2-2-c",
    label: "Bedrageri",
    foaReference: "forsyningsforskriften § 20-2 annet ledd bokstav c",
    mandatory: false,
  },
  {
    key: "ff-20-2-2-d",
    label: "Terrorhandlinger eller straffbare handlinger med forbindelse til terroraktivitet",
    foaReference: "forsyningsforskriften § 20-2 annet ledd bokstav d",
    mandatory: false,
  },
  {
    key: "ff-20-2-2-e",
    label: "Hvitvasking av penger eller finansiering av terrorisme",
    foaReference: "forsyningsforskriften § 20-2 annet ledd bokstav e",
    mandatory: false,
  },
  {
    key: "ff-20-2-2-f",
    label: "Barnearbeid og andre former for menneskehandel",
    foaReference: "forsyningsforskriften § 20-2 annet ledd bokstav f",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-a",
    label: "Ikke oppfylt forpliktelser til å betale skatter, avgifter og trygdeavgifter",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav a",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-b",
    label: "Konkurs, insolvensbehandling, avvikling eller tilsvarende prosess",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav b",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-c",
    label: "Alvorlige eller gjentatte brudd på miljø, arbeidsforhold eller sosiale forhold",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav c",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-d",
    label: "Interessekonflikt som kan påvirke kontraktoppfyllelsen",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav d",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-e",
    label: "Avtale med hensikt å vri konkurransen",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav e",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-f",
    label: "Vesentlig kontraktsbrudd ved en tidligere kontrakt med en oppdragsgiver",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav f",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-g",
    label: "Grovt uriktige eller misvisende opplysninger",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav g",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-h",
    label: "Utilbørlig påvirkning eller tilgang til fortrolige opplysninger",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav h",
    mandatory: false,
  },
  {
    key: "ff-20-2-3-i",
    label: "Alvorlige feil som medfører tvil om yrkesmessig integritet",
    foaReference: "forsyningsforskriften § 20-2 tredje ledd bokstav i",
    mandatory: false,
  },
];

const UTILITIES_PUBLIC_AUTHORITIES: BuyerLegalType[] = [
  BuyerLegalType.CentralGovernment,
  BuyerLegalType.County,
  BuyerLegalType.Municipality,
  BuyerLegalType.RegionalOrLocal,
  BuyerLegalType.PublicLawBody,
  BuyerLegalType.PublicLawBodyCentral,
  BuyerLegalType.PublicLawBodyRegional,
  BuyerLegalType.PublicLawBodyLocal,
  BuyerLegalType.GroupOfPublicAuthorities,
];

// Forsyningsforskriften § 20-2 (4): buyers under § 1-2 (1) a–d shall reject for the convictions in (2), and for a
// tax breach under (3) a once a final judgment or decision has settled it; (3) a then covers the other cases.
const UTILITIES_SETTLED_TAX_GROUND: RejectionGroundDto = {
  key: "ff-20-2-4-b",
  label:
    "Ikke oppfylt forpliktelser til å betale skatter, avgifter og trygdeavgifter, slått fast ved rettskraftig dom eller endelig forvaltningsvedtak",
  foaReference: "forsyningsforskriften § 20-2 fjerde ledd bokstav b, jf. tredje ledd bokstav a",
  mandatory: true,
};

const asPublicAuthorityGround = (ground: RejectionGroundDto): RejectionGroundDto => {
  if (ground.key.startsWith("ff-20-2-2-")) return { ...ground, mandatory: true };
  if (ground.key === "ff-20-2-3-a") return { ...ground, label: `${ground.label} i andre tilfeller` };
  return ground;
};

// Mirrors RejectionRules.buyerIsUtilitiesPublicAuthority on the server, which treats an unknown buyer as one.
const utilitiesSupplierGroup = (buyerLegalType?: BuyerLegalType | null): FoaRejectionGroundGroup => {
  const convictionsMandatory = buyerLegalType == null || UTILITIES_PUBLIC_AUTHORITIES.includes(buyerLegalType);
  return {
    subject: "supplier",
    keyPrefix: "ff-20-2-",
    mandatoryDescription: convictionsMandatory
      ? "forsyningsforskriften § 20-2 første og annet ledd, jf. fjerde ledd"
      : "forsyningsforskriften § 20-2 første ledd",
    optionalDescription: convictionsMandatory
      ? "forsyningsforskriften § 20-2 tredje ledd"
      : "forsyningsforskriften § 20-2 annet og tredje ledd",
    grounds: convictionsMandatory
      ? [...UTILITIES_SUPPLIER_GROUNDS.map(asPublicAuthorityGround), UTILITIES_SETTLED_TAX_GROUND]
      : UTILITIES_SUPPLIER_GROUNDS,
  };
};

// Labels are the regulation's own wording (Lovdata, FOA § 24-8), prefixed with the "tilbud som" it hangs on.
const TENDER_REJECTION_GROUND_GROUP: FoaRejectionGroundGroup = {
  subject: "tender",
  keyPrefix: "foa-24-8-",
  mandatoryDescription: "FOA § 24-8 første ledd",
  optionalDescription: "FOA § 24-8 annet ledd",
  grounds: [
    {
      key: "foa-24-8-1-a",
      label: "Tilbud som ikke kan anses bindende",
      foaReference: "FOA § 24-8 første ledd bokstav a",
      mandatory: true,
    },
    {
      key: "foa-24-8-1-b",
      label: "Tilbud som inneholder vesentlige avvik fra anskaffelsesdokumentene",
      foaReference: "FOA § 24-8 første ledd bokstav b",
      mandatory: true,
    },
    {
      key: "foa-24-8-1-c",
      label:
        "Tilbud som er unormalt lavt i forhold til ytelsen, jf. § 24-9, fordi det ikke oppfyller bestemmelser om miljø, arbeidsforhold og sosiale forhold som følger av nasjonale regler, EØS-regler, tariffavtaler eller internasjonale avtaler som angitt i vedlegg 5",
      foaReference: "FOA § 24-8 første ledd bokstav c, jf. § 24-9",
      mandatory: true,
    },
    {
      key: "foa-24-8-2-a",
      label:
        "Tilbud som inneholder avvik fra anskaffelsesdokumentene eller uklarheter som ikke må anses ubetydelige",
      foaReference: "FOA § 24-8 annet ledd bokstav a",
      mandatory: false,
    },
    {
      key: "foa-24-8-2-b",
      label:
        "Tilbud som er unormalt lavt i forhold til ytelsen, jf. § 24-9, av andre grunner enn dem som er nevnt i første ledd bokstav c",
      foaReference: "FOA § 24-8 annet ledd bokstav b, jf. § 24-9",
      mandatory: false,
    },
    {
      key: "foa-24-8-2-c",
      label:
        "Tilbud som ikke oppfyller bestemmelser om miljø, arbeidsforhold og sosiale forhold som følger av nasjonale regler, EØS-regler, tariffavtaler eller internasjonale avtaler som angitt i vedlegg 5",
      foaReference: "FOA § 24-8 annet ledd bokstav c",
      mandatory: false,
    },
    {
      key: "foa-24-8-2-d",
      label:
        "Tilbud som overstiger oppdragsgiverens budsjett som er dokumentert fastsatt før iverksettelse av konkurransen",
      foaReference: "FOA § 24-8 annet ledd bokstav d",
      mandatory: false,
    },
    {
      key: "foa-24-8-2-e",
      label:
        "Tilbud som oppdragsgiveren for øvrig ikke kan akseptere fordi det foreligger lignende forhold som i bokstav a til d",
      foaReference: "FOA § 24-8 annet ledd bokstav e",
      mandatory: false,
    },
  ],
};

// FOA § 9-6: first paragraph mirrors § 24-8 (1) a and b; the second borrows § 24-8 (2) wholesale.
const PART_II_TENDER_REJECTION_GROUND_GROUP: FoaRejectionGroundGroup = {
  subject: "tender",
  keyPrefix: "foa-9-6-",
  mandatoryDescription: "FOA § 9-6 første ledd",
  optionalDescription: "FOA § 9-6 annet ledd",
  grounds: [
    {
      key: "foa-9-6-1-a",
      label: "Tilbud som ikke kan anses bindende",
      foaReference: "FOA § 9-6 første ledd bokstav a",
      mandatory: true,
    },
    {
      key: "foa-9-6-1-b",
      label: "Tilbud som inneholder vesentlige avvik fra anskaffelsesdokumentene",
      foaReference: "FOA § 9-6 første ledd bokstav b",
      mandatory: true,
    },
    {
      key: "foa-9-6-2-a",
      label:
        "Tilbud som inneholder avvik fra anskaffelsesdokumentene eller uklarheter som ikke må anses ubetydelige",
      foaReference: "FOA § 9-6 annet ledd, jf. § 24-8 annet ledd bokstav a",
      mandatory: false,
    },
    {
      key: "foa-9-6-2-b",
      label:
        "Tilbud som er unormalt lavt i forhold til ytelsen, jf. § 24-9, av andre grunner enn dem som er nevnt i første ledd bokstav c",
      foaReference: "FOA § 9-6 annet ledd, jf. § 24-8 annet ledd bokstav b",
      mandatory: false,
    },
    {
      key: "foa-9-6-2-c",
      label:
        "Tilbud som ikke oppfyller bestemmelser om miljø, arbeidsforhold og sosiale forhold som følger av nasjonale regler, EØS-regler, tariffavtaler eller internasjonale avtaler som angitt i vedlegg 5",
      foaReference: "FOA § 9-6 annet ledd, jf. § 24-8 annet ledd bokstav c",
      mandatory: false,
    },
    {
      key: "foa-9-6-2-d",
      label:
        "Tilbud som overstiger oppdragsgiverens budsjett som er dokumentert fastsatt før iverksettelse av konkurransen",
      foaReference: "FOA § 9-6 annet ledd, jf. § 24-8 annet ledd bokstav d",
      mandatory: false,
    },
    {
      key: "foa-9-6-2-e",
      label:
        "Tilbud som oppdragsgiveren for øvrig ikke kan akseptere fordi det foreligger lignende forhold som i bokstav a til d",
      foaReference: "FOA § 9-6 annet ledd, jf. § 24-8 annet ledd bokstav e",
      mandatory: false,
    },
  ],
};

export const PROCUREMENT_DOCUMENTS_BASIS =
  "Anskaffelsesdokumentene og de grunnleggende prinsippene i anskaffelsesloven § 4";

export const citesRegulation = (ground: RejectionGroundDto) => ground.foaReference.trim().length > 0;

// Mirrors RejectionGroundCatalog.mutuallyExclusive on the server: one tax breach is either settled by a final
// judgment or decision, or it is not, so a rejection cites one ground of each pair.
const MUTUALLY_EXCLUSIVE_GROUND_KEYS: string[][] = [
  ["foa-24-2-1-b", "foa-24-2-3-a"],
  ["ff-20-2-4-b", "ff-20-2-3-a"],
];

export const groundKeysExcludedBy = (key: string): string[] =>
  MUTUALLY_EXCLUSIVE_GROUND_KEYS.filter((pair) => pair.includes(key)).flatMap((pair) =>
    pair.filter((other) => other !== key),
  );

// FOA § 5-1 (4) and § 30-1: Part IV is governed by Parts I and IV only, so no chapter 24 section is cited.
const asPartIvReasons = (groups: FoaRejectionGroundGroup[]): RejectionGroundGroup[] =>
  (["supplier", "tender"] as const)
    .map((subject) => groups.filter((group) => group.subject === subject))
    .filter((ofSubject) => ofSubject.length > 0)
    .map((ofSubject) => ({
      kind: "reasons",
      subject: ofSubject[0].subject,
      description: PROCUREMENT_DOCUMENTS_BASIS,
      grounds: ofSubject.flatMap((group) => group.grounds).map((ground) => ({ ...ground, foaReference: "" })),
    }));

export const isTenderRejectionGround = (ground: RejectionGroundDto) =>
  [TENDER_REJECTION_GROUND_GROUP, PART_II_TENDER_REJECTION_GROUND_GROUP].some((group) =>
    ground.key.startsWith(group.keyPrefix),
  );

const QUALIFICATION_REQUIREMENT_GROUND_KEYS = ["foa-24-2-1-a", "foa-9-5-1-a", "ff-20-2-1-a"];

export const getQualificationRequirementGroundKey = (
  foaPart?: FoaPart | null,
  regime: RejectionRegime = {},
): string | undefined =>
  getApplicableRejectionGroundGroups(foaPart, { regime })
    .flatMap((group) => group.grounds)
    .find((ground) => QUALIFICATION_REQUIREMENT_GROUND_KEYS.includes(ground.key))
    ?.key;

export const getApplicableRejectionGroundGroups = (
  foaPart?: FoaPart | null,
  { includeTenderGrounds = false, regime = {} }: { includeTenderGrounds?: boolean; regime?: RejectionRegime } = {},
): RejectionGroundGroup[] => {
  if (regime.regulation === Regulation.Forsyningsforskriften) {
    return getApplicableUtilitiesGroups(foaPart, includeTenderGrounds, regime.buyerLegalType);
  }
  return foaPart === FoaPart.PartIv
    ? asPartIvReasons(getApplicableFoaPartGroups(FoaPart.PartIii, includeTenderGrounds))
    : getApplicableFoaPartGroups(foaPart, includeTenderGrounds).flatMap(splitByRejectionType);
};

// Forsyningsforskriften § 5-1 (3): Part III (chapter 26) has no rejection rule of its own; chapter 20 is Part II.
const getApplicableUtilitiesGroups = (
  foaPart: FoaPart | null | undefined,
  includeTenderGrounds: boolean,
  buyerLegalType: BuyerLegalType | null | undefined,
): RejectionGroundGroup[] => {
  const supplier = utilitiesSupplierGroup(buyerLegalType);
  switch (foaPart) {
    case FoaPart.PartIi:
      return [supplier, ...(includeTenderGrounds ? [PART_II_TENDER_REJECTION_GROUND_GROUP] : [])].flatMap(
        splitByRejectionType,
      );
    case FoaPart.PartIii:
      return asPartIvReasons([supplier, ...(includeTenderGrounds ? [TENDER_REJECTION_GROUND_GROUP] : [])]);
    case undefined:
    case null:
      return splitByRejectionType(supplier);
    case FoaPart.PartI:
    case FoaPart.PartIv:
    case FoaPart.OutsideFoa:
      return [];
  }
};

const getApplicableFoaPartGroups = (
  foaPart: FoaPart | null | undefined,
  includeTenderGrounds: boolean,
): FoaRejectionGroundGroup[] => {
  const withTenderGrounds = (groups: FoaRejectionGroundGroup[], ...tenderGroups: FoaRejectionGroundGroup[]) =>
    includeTenderGrounds ? [...groups, ...tenderGroups] : groups;
  switch (foaPart) {
    case FoaPart.PartIi:
      return withTenderGrounds(
        SUPPLIER_REJECTION_GROUND_GROUPS.filter((group) => group.keyPrefix === "foa-9-5-"),
        PART_II_TENDER_REJECTION_GROUND_GROUP,
      );
    case FoaPart.PartIii:
      return withTenderGrounds(
        SUPPLIER_REJECTION_GROUND_GROUPS.filter((group) => group.keyPrefix === "foa-24-2-"),
        TENDER_REJECTION_GROUND_GROUP,
      );
    case undefined:
    case null:
      return withTenderGrounds(
        SUPPLIER_REJECTION_GROUND_GROUPS,
        TENDER_REJECTION_GROUND_GROUP,
        PART_II_TENDER_REJECTION_GROUND_GROUP,
      );
    case FoaPart.PartI:
    case FoaPart.PartIv:
    case FoaPart.OutsideFoa:
      return [];
  }
};

const splitByRejectionType = (group: FoaRejectionGroundGroup): RejectionGroundGroup[] =>
  [
    {
      kind: "mandatory" as const,
      subject: group.subject,
      description: group.mandatoryDescription,
      grounds: group.grounds.filter((ground) => ground.mandatory),
    },
    {
      kind: "optional" as const,
      subject: group.subject,
      description: group.optionalDescription,
      grounds: group.grounds.filter((ground) => !ground.mandatory),
    },
  ].filter((split) => split.grounds.length > 0);
